<template>
  <section class="campsite-water-quality">
    <translate tag="h3"> Water quality at nearby bathing waters </translate>
    <div class="water-quality-scroller">
      <table class="water-quality-table">
        <thead>
          <tr>
            <translate tag="th" class="name-col"> Name </translate>
            <translate tag="th"> Distance </translate>
            <translate tag="th"> Type </translate>
            <th
              v-for="year in waterQualityYears"
              :key="year"
              class="distance-col"
            >
              {{ year }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(beach, index) in points" :key="index">
            <td class="beach-name">
              {{ beach.name }}
              <span class="beach-location">{{ beach.location }}</span>
            </td>
            <translate tag="td" :tt="{ value: beach.distance }">
              {value, distance, short}
            </translate>
            <td>
              {{ beach.waterType }}
            </td>
            <td
              v-for="year in waterQualityYears"
              :key="year"
              :class="getRatingClass(year, beach.waterQuality)"
              class="beach-rating"
            >
              {{ getRatingName(year, beach.waterQuality) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p class="data-source">
      <translate>Data source:</translate>
      <translate
        tag="a"
        href="https://www.eea.europa.eu/themes/water/europes-seas-and-coasts/assessments/state-of-bathing-water"
        target="_blank"
        rel="nofollow noopener"
      >
        European Environment Agency
      </translate>
    </p>
  </section>
</template>

<script lang="ts">
import { WaterQualityDetails } from '../../../campsite-details/types'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'

const DISPLAY_YEARS = 4

@Component
class CampsiteWaterQuality extends Vue {
  @Prop()
  points: WaterQualityDetails[]

  get waterQualityYears() {
    const years: number[] = []
    this.points.forEach((point) => {
      Object.keys(point.waterQuality).forEach((year) => {
        years.push(Number(year))
      })
    })
    return [...new Set(years)].sort((a, b) => b - a).slice(0, DISPLAY_YEARS)
  }

  getRatingClass(year: number, quality: WaterQualityDetails['waterQuality']) {
    return quality[year] ? quality[year].rating : 'not-available'
  }

  getRatingName(year: number, quality: WaterQualityDetails['waterQuality']) {
    return quality[year] ? quality[year].rating_name : this.$t('N/A')
  }
}

export default toNative(CampsiteWaterQuality)
</script>

<style scoped>
.water-quality-scroller {
  overflow-x: auto;
  width: calc(100vw - 1.5em);
  position: relative;
}

th,
td {
  text-align: center;
  vertical-align: middle;
  padding: 0.3em;
}

.name-col {
  text-align: left;
}

.beach-name {
  font-weight: bold;
  line-height: 1.2;
  text-align: left;
}

.water-quality-table {
  font-size: var(--small);
}

.beach-location {
  font-weight: normal;
  font-style: italic;
  font-size: var(--small);
  color: var(--colour-text-light);
  display: block;
}

.beach-rating {
  color: white;
  text-align: center;
  background-color: #999;
  border-right: 1px solid white;
}

.beach-rating.excellent {
  background-color: #259ffc;
}

.beach-rating.good {
  background-color: #80ba25;
}

.beach-rating.poor {
  background-color: #cc271a;
}

.data-source {
  font-size: var(--small);
  margin: 1em 0;
}

.data-source,
.data-source a {
  font-weight: normal;
  color: var(--colour-text-light);
}

/* mobile */
@media only screen and (max-width: 768px) {
  .campsite-water-quality:before {
    content: '';
    display: block;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 1) 100%
    );
    position: absolute;
    width: 3em;
    height: calc(100% - 2em);
    bottom: 0;
    right: -1.8em;
    z-index: 1;
  }
}
</style>
